import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { getCatalogIsInstantAutopay } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getIsRegistered,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { CatalogRegistrationMessagesExperiment } from './CatalogRegistrationMessagesExperiment';

type Props = {
    catalogId: number;
    isLive: boolean;
};

export const CatalogRegistrationButton = ({ catalogId, isLive }: Props) => {
    const formatMessage = useAppFormatMessage();
    const dispatch = useAppDispatch();

    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const registerForCatalog = useCallback(() => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    }, [catalogId, dispatch, isInstantAutopay, modalContext, trackRegisterForAuctionPressedAnalytics]);

    if (suspended) {
        return (
            <Button
                className="w-full"
                data-testid="catalogAccountSuspendedButton"
                disabled
                variant="filled"
            >
                {formatMessage(['account_suspended'])}
            </Button>
        );
    }

    if (!registered) {
        return (
            <Button
                className="w-full"
                data-testid="liveCatalogRegisterButton"
                onClick={registerForCatalog}
                style={isLive ? 'critical' : 'primary'}
                variant={isLive ? 'outlined' : 'filled'}
            >
                {formatMessage(['register_for_auction'])}
            </Button>
        );
    }

    return (
        <CatalogRegistrationMessagesExperiment
            catalogId={catalogId}
            className="w-full"
        />
    );
};
